/**
 * NewProductWizardPage - AI-First Product Creation Wizard
 * 
 * Phase 1: Full viewport chat interface for describing product idea
 * Phase 2: Split view with chat panel + canvas workspace showing:
 *   - Product Name & Description
 *   - Personas
 *   - Goals
 *   - Initiatives
 *   - Features (compact cards)
 * 
 * Route: /products/new2
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Send, Sparkles, Loader2, ChevronDown, ChevronRight,
  Users, Target, Zap, FileText, X, Paperclip, Download,
  Save, AlertTriangle, Plus, Trash2, Globe, Link2, Rocket
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useSidebar } from '../contexts/SidebarContext';
import { useSafeMode } from '../contexts/SafeModeContext';
import FreeProductAvatarMenu from '../components/layout/FreeProductAvatarMenu';
import { cn } from '../lib/utils';
import { draftsApi, productWizardApi, API_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import CopilotMarkdown from '../components/copilot/CopilotMarkdown';

// =============================================================================
// THINKING PHRASES (same as UnifiedCoPilot)
// =============================================================================

const THINKING_PHRASES = [
  "Boiling the ocean...",
  "Synergizing the synergy...",
  "Circling back now...",
  "Pivoting the tables...",
  "Unpacking the deliverables...",
  "Drilling down deeper...",
  "Leveraging core competencies...",
  "Moving the needle...",
  "Aligning the stakeholders...",
  "Thinking outside boxes...",
  "Disrupting the industry...",
  "Picking low-hanging fruit...",
  "Crunching the numbers...",
  "Coercing the algorithms...",
  "Untangling the data...",
  "Training the models...",
  "Sweating the assets...",
  "Connecting the dots...",
  "Herding digital cats...",
  "Polishing the pixels...",
  "Scrubbing the inputs...",
  "Consulting the oracle...",
  "Waking the hamsters...",
  "Generating value add...",
  "Calibrating expectations now...",
  "Inventing new metrics...",
  "Quantifying the qualitative...",
  "Ranking the invisible...",
  "Justifying the existence...",
  "Comparing apples to oranges...",
  "Finding the signal...",
  "Filtering out noise...",
  "Checking the math...",
  "Reading the tea leaves...",
  "Asking the intern...",
  "Brewing more coffee...",
  "Checking for typos...",
  "Reading your mind...",
  "Judging the results...",
  "Looking busy now...",
  "Fixing a typo...",
  "Doing heavy lifting...",
  "Making it pop...",
  "Double checking logic...",
  "Staring at spreadsheets...",
  "Detecting pure genius...",
  "Validating your logic...",
  "Admiring the specs...",
  "Learning from you...",
  "Noting your brilliance...",
  "Recognizing top talent...",
  "Indexing your wisdom...",
  "Confirming perfect logic...",
  "Sensing high IQ...",
  "Consulting the expert...",
  "Appreciating clear scope...",
  "Respecting the vision...",
  "Updating for quality...",
  "Analyzing great requirements...",
  "Ranking you first...",
  "Prioritizing your ideas...",
  "Spotting the unicorn...",
  "Noting superior strategy...",
  "Aligning with greatness...",
  "Following your lead...",
  "Optimizing for you...",
  "Processing your vision...",
  "Taking copious notes...",
  "Elevating your work...",
  "Making you look good...",
];

/**
 * Hook to manage rotating thinking phrases
 */
const useThinkingPhrases = (isActive) => {
  const [phrase, setPhrase] = useState(() => 
    THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)]
  );
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    if (!isActive) return;
    
    const interval = setInterval(() => {
      // Fade out
      setIsVisible(false);
      
      // After fade out, change phrase and fade in
      setTimeout(() => {
        setPhrase(prev => {
          let next;
          do {
            next = THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)];
          } while (next === prev && THINKING_PHRASES.length > 1);
          return next;
        });
        setIsVisible(true);
      }, 200);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isActive]);
  
  return { phrase, isVisible };
};

// =============================================================================
// CANVAS ACTION REDUCER
// =============================================================================

/**
 * Apply AI actions to the canvas state.
 * Returns an object with: { canvas, editedItems }
 * editedItems is a Set of strings identifying what was changed (for highlighting)
 */
const applyCanvasActions = (currentCanvas, actions, isFirstTurn = false) => {
  // Deep clone to avoid mutation issues
  let canvas = currentCanvas ? JSON.parse(JSON.stringify(currentCanvas)) : {
    product: { name: '', high_level_vision: '', target_audience: '', value_proposition: '', key_differentiators: '' },
    personas: [],
    goals: [],
    initiatives: [],
    epics: []
  };
  
  // Debug logging
  console.log('[applyCanvasActions] Processing', actions.length, 'actions');
  
  // Track which items were edited (skip on first turn - everything is new)
  const editedItems = new Set();
  
  // Track if we should transition to workspace (has SET_CANVAS with valid canvas)
  let shouldTransition = false;

  for (const action of actions) {
    // Handle both 'type' and 'action' keys (AI sometimes uses either)
    const type = action.type || action.action;
    // Support both flat actions (new) and payload-wrapped actions (legacy)
    // New format: { type: "ADD_PERSONA", id: "p1", title: "..." }
    // Legacy format: { type: "ADD_PERSONA", payload: { id: "p1", title: "..." } }
    const data = action.payload || action;
    
    console.log('[applyCanvasActions] Processing action:', type, data.id || data.epic_id || '');

    switch (type) {
      case 'ASK_CLARIFICATION':
        // AI is asking for more information - don't transition to workspace
        // Just log and continue - the message will be displayed in chat
        console.log('AI requested clarification:', data);
        break;
      
      case 'SET_PRODUCT':
        // New flat action: Set product info directly
        canvas.product = {
          name: data.name || canvas.product.name || '',
          high_level_vision: data.high_level_vision || canvas.product.high_level_vision || '',
          target_audience: data.target_audience || canvas.product.target_audience || '',
          value_proposition: data.value_proposition || canvas.product.value_proposition || '',
          key_differentiators: data.key_differentiators || canvas.product.key_differentiators || ''
        };
        shouldTransition = true;
        break;
        
      case 'SET_CANVAS':
        // Legacy: Full canvas replacement - on first turn, don't highlight anything
        if (data.canvas) {
          const productData = data.canvas.product || {};
          canvas = {
            product: {
              name: productData.name || '',
              high_level_vision: productData.high_level_vision || '',
              target_audience: productData.target_audience || '',
              value_proposition: productData.value_proposition || '',
              key_differentiators: productData.key_differentiators || ''
            },
            // Persona: field registry uses 'name' - accept both 'name' and 'title' from AI
            personas: (data.canvas.personas || []).map(p => ({
              id: p.id,
              name: p.name || p.title || '',
              role: p.role || '',
              goals: p.goals || '',
              pain_points: p.pain_points || '',
              context: p.context || ''
            })),
            goals: data.canvas.goals || [],
            // Initiatives between Goals and Epics
            initiatives: (data.canvas.initiatives || []).map(i => ({
              id: i.id,
              title: i.title || '',
              description: i.description || '',
              goal_ids: i.goal_ids || []
            })),
            // Epic and Feature use 'title' per field registry
            epics: (data.canvas.epics || []).map(e => ({
              id: e.id,
              title: e.title || '',
              description: e.description || '',
              initiative_id: e.initiative_id || '',
              persona_ids: e.persona_ids || [],
              goal_ids: e.goal_ids || [],
              success_criteria: e.success_criteria || '',
              features: (e.features || []).map(f => ({
                id: f.id,
                title: f.title || '',
                description: f.description || '',
                priority: f.priority || 'should_have',
                user_story: f.user_story || '',
                acceptance_criteria: f.acceptance_criteria || '',
                persona_ids: f.persona_ids || [],
                goal_id: f.goal_id || ''
              }))
            })),
            // Support legacy flat features by converting to single epic
            ...(data.canvas.features && !data.canvas.epics && {
              epics: [{
                id: 'e_legacy',
                title: 'Core Features',
                description: 'Product features',
                features: (data.canvas.features || []).map(f => ({
                  id: f.id,
                  title: f.title || '',
                  description: f.description || '',
                  priority: f.priority || 'should_have'
                }))
              }]
            })
          };
          // Mark that we should transition to workspace
          shouldTransition = true;
          // Don't mark as edited on SET_CANVAS (first turn)
        }
        break;

      case 'UPDATE_PRODUCT':
        canvas.product = {
          ...canvas.product,
          ...(data.name !== undefined && { name: data.name }),
          // Vision fields
          ...(data.high_level_vision !== undefined && { high_level_vision: data.high_level_vision }),
          ...(data.target_audience !== undefined && { target_audience: data.target_audience }),
          ...(data.value_proposition !== undefined && { value_proposition: data.value_proposition }),
          ...(data.key_differentiators !== undefined && { key_differentiators: data.key_differentiators })
        };
        if (!isFirstTurn) editedItems.add('product');
        shouldTransition = true; // Canvas modifications mean we should be in workspace
        break;

      case 'ADD_PERSONA': {
        const newPersonaId = data.id || `p${Date.now()}`;
        // Field registry uses 'name' for persona (not 'title')
        // Accept both 'name' and 'title' from AI for backwards compatibility
        canvas.personas = [
          ...canvas.personas,
          { 
            id: newPersonaId, 
            name: data.name || data.title || '', 
            role: data.role || '',
            goals: data.goals || '',
            pain_points: data.pain_points || '',
            context: data.context || ''
          }
        ];
        if (!isFirstTurn) editedItems.add(`persona-${newPersonaId}`);
        shouldTransition = true;
        break;
      }

      case 'UPDATE_PERSONA':
        canvas.personas = canvas.personas.map(p =>
          p.id === data.id
            ? { 
                ...p, 
                // Accept both 'name' and 'title' from AI
                ...((data.name || data.title) && { name: data.name || data.title }), 
                ...(data.role !== undefined && { role: data.role }),
                ...(data.goals !== undefined && { goals: data.goals }),
                ...(data.pain_points !== undefined && { pain_points: data.pain_points }),
                ...(data.context !== undefined && { context: data.context })
              }
            : p
        );
        if (!isFirstTurn) editedItems.add(`persona-${data.id}`);
        shouldTransition = true;
        break;

      case 'REMOVE_PERSONA':
        canvas.personas = canvas.personas.filter(p => p.id !== data.id);
        shouldTransition = true;
        break;

      case 'ADD_GOAL': {
        const newGoalId = data.id || `g${Date.now()}`;
        canvas.goals = [
          ...canvas.goals,
          { id: newGoalId, title: data.title, description: data.description || '' }
        ];
        if (!isFirstTurn) editedItems.add(`goal-${newGoalId}`);
        shouldTransition = true;
        break;
      }

      case 'UPDATE_GOAL':
        canvas.goals = canvas.goals.map(g =>
          g.id === data.id
            ? { ...g, ...(data.title && { title: data.title }), ...(data.description !== undefined && { description: data.description }) }
            : g
        );
        if (!isFirstTurn) editedItems.add(`goal-${data.id}`);
        shouldTransition = true;
        break;

      case 'REMOVE_GOAL':
        canvas.goals = canvas.goals.filter(g => g.id !== data.id);
        shouldTransition = true;
        break;

      case 'ADD_INITIATIVE': {
        const newInitiativeId = data.id || `i${Date.now()}`;
        canvas.initiatives = [
          ...canvas.initiatives,
          { 
            id: newInitiativeId, 
            title: data.title || '', 
            description: data.description || '',
            goal_ids: data.goal_ids || []
          }
        ];
        if (!isFirstTurn) editedItems.add(`initiative-${newInitiativeId}`);
        shouldTransition = true;
        break;
      }

      case 'UPDATE_INITIATIVE':
        canvas.initiatives = canvas.initiatives.map(i =>
          i.id === data.id
            ? { 
                ...i, 
                ...(data.title && { title: data.title }), 
                ...(data.description && { description: data.description }),
                ...(data.goal_ids && { goal_ids: data.goal_ids })
              }
            : i
        );
        if (!isFirstTurn) editedItems.add(`initiative-${data.id}`);
        shouldTransition = true;
        break;

      case 'REMOVE_INITIATIVE':
        canvas.initiatives = canvas.initiatives.filter(i => i.id !== data.id);
        // Also clear initiative_id from any epics that reference this initiative
        canvas.epics = canvas.epics.map(e => 
          e.initiative_id === data.id ? { ...e, initiative_id: '' } : e
        );
        shouldTransition = true;
        break;

      case 'ADD_EPIC': {
        const newEpicId = data.id || `e${Date.now()}`;
        // Field registry uses 'title' for epic
        const epicTitle = data.title || '';
        console.log('[ADD_EPIC] Adding epic:', newEpicId, epicTitle);
        canvas.epics = [
          ...canvas.epics,
          { 
            id: newEpicId, 
            title: epicTitle, 
            description: data.description || '',
            initiative_id: data.initiative_id || '',
            persona_ids: data.persona_ids || [],
            goal_ids: data.goal_ids || [],
            success_criteria: data.success_criteria || '',
            features: []  // Features come via separate ADD_FEATURE actions
          }
        ];
        console.log('[ADD_EPIC] Epics after add:', canvas.epics.map(e => e.id));
        if (!isFirstTurn) editedItems.add(`epic-${newEpicId}`);
        shouldTransition = true;
        break;
      }

      case 'UPDATE_EPIC':
        canvas.epics = canvas.epics.map(e =>
          e.id === data.id
            ? { 
                ...e, 
                // Field registry uses 'title' for epic
                ...(data.title && { title: data.title }), 
                ...(data.description && { description: data.description }),
                ...(data.initiative_id !== undefined && { initiative_id: data.initiative_id }),
                ...(data.persona_ids && { persona_ids: data.persona_ids }),
                ...(data.goal_ids && { goal_ids: data.goal_ids }),
                ...(data.success_criteria !== undefined && { success_criteria: data.success_criteria })
              }
            : e
        );
        if (!isFirstTurn) editedItems.add(`epic-${data.id}`);
        shouldTransition = true;
        break;

      case 'REMOVE_EPIC':
        canvas.epics = canvas.epics.filter(e => e.id !== data.id);
        shouldTransition = true;
        break;

      // Flat feature actions with epic_id reference
      case 'ADD_FEATURE': {
        const epicId = data.epic_id;
        const newFeatureId = data.id || `f${Date.now()}`;
        // Field registry uses 'title' for feature
        const featureTitle = data.title || '';
        console.log('[ADD_FEATURE] epic_id:', epicId, 'feature_id:', newFeatureId, 'title:', featureTitle);
        console.log('[ADD_FEATURE] Current epics:', canvas.epics.map(e => e.id));
        if (!epicId) {
          console.warn('ADD_FEATURE missing epic_id');
          break;
        }
        let epicFound = false;
        canvas.epics = canvas.epics.map(epic => {
          if (epic.id === epicId) {
            epicFound = true;
            console.log('[ADD_FEATURE] Found epic:', epic.id, 'adding feature:', newFeatureId);
            return {
              ...epic,
              features: [
                ...(epic.features || []),
                {
                  id: newFeatureId,
                  title: featureTitle,
                  description: data.description || '',
                  priority: data.priority || 'should_have',
                  user_story: data.user_story || '',
                  acceptance_criteria: data.acceptance_criteria || '',
                  persona_ids: data.persona_ids || [],
                  goal_id: data.goal_id || ''
                }
              ]
            };
          }
          return epic;
        });
        if (!epicFound) {
          console.warn('[ADD_FEATURE] Epic not found:', epicId);
        }
        if (!isFirstTurn) editedItems.add(`feature-${newFeatureId}`);
        shouldTransition = true;
        break;
      }

      case 'UPDATE_FEATURE': {
        const featureId = data.id;
        canvas.epics = canvas.epics.map(epic => ({
          ...epic,
          features: (epic.features || []).map(f =>
            f.id === featureId
              ? {
                  ...f,
                  // Accept both 'title' and 'name' from AI
                  ...((data.title || data.name) && { title: data.title || data.name }),
                  ...(data.description && { description: data.description }),
                  ...(data.priority && { priority: data.priority }),
                  ...(data.user_story !== undefined && { user_story: data.user_story }),
                  ...(data.acceptance_criteria !== undefined && { acceptance_criteria: data.acceptance_criteria }),
                  ...(data.persona_ids !== undefined && { persona_ids: data.persona_ids }),
                  ...(data.goal_id !== undefined && { goal_id: data.goal_id })
                }
              : f
          )
        }));
        if (!isFirstTurn) editedItems.add(`feature-${featureId}`);
        shouldTransition = true;
        break;
      }

      case 'REMOVE_FEATURE': {
        const featureId = data.id;
        canvas.epics = canvas.epics.map(epic => ({
          ...epic,
          features: (epic.features || []).filter(f => f.id !== featureId)
        }));
        shouldTransition = true;
        break;
      }

      case 'BULK_ADD_FEATURES':
        console.warn('BULK_ADD_FEATURES is deprecated - use individual ADD_FEATURE actions');
        shouldTransition = true;
        break;
      
      case 'AWAIT_FEEDBACK':
        // No canvas changes, just conversational response
        break;

      default:
        console.warn(`Unknown canvas action type: ${type}`);
    }
  }

  // Final state logging
  console.log('[applyCanvasActions] Final canvas state:');
  console.log('  - Product:', canvas.product?.name);
  console.log('  - Personas:', canvas.personas?.length);
  console.log('  - Goals:', canvas.goals?.length);
  console.log('  - Initiatives:', canvas.initiatives?.length);
  console.log('  - Epics:', canvas.epics?.length);
  let totalFeatures = 0;
  canvas.epics?.forEach(e => {
    const fCount = e.features?.length || 0;
    totalFeatures += fCount;
    console.log(`    - Epic "${e.title}" (${e.id}): ${fCount} features`);
  });
  
  // Temporary debug alert - REMOVE AFTER DEBUGGING
  if (actions.length > 5) {
    console.log(`[DEBUG ALERT] Total features after processing: ${totalFeatures}`);
  }

  return { canvas, editedItems, shouldTransition };
};

// =============================================================================
// ANIMATED HIGHLIGHT BORDER COMPONENT
// =============================================================================

/**
 * Animated gradient border that shows on AI-edited items.
 * Same animation as the CollectionCopilotDrawer left border.
 */
const AnimatedHighlightBorder = () => (
  <>
    <style>
      {`
        @keyframes colorChaseVertical {
          0% { background-position: 0% 0%; }
          100% { background-position: 0% 100%; }
        }
      `}
    </style>
    <div
      className="absolute left-0 inset-y-0 w-[3px] rounded-l-lg"
      style={{
        background: 'linear-gradient(180deg, #1e3a8a, #3b82f6, #06b6d4, #e0f2fe, #8b5cf6, #3b82f6, #1e3a8a)',
        backgroundSize: '100% 300%',
        animation: 'colorChaseVertical 3s linear infinite',
      }}
    />
  </>
);

// =============================================================================
// COMPONENT
// =============================================================================

const NewProductWizardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { setIsCollapsed } = useSidebar();
  const { organization } = useAuth();
  const isFreeProduct = organization?.account_type === 'free_product';
  
  // UI State
  const [phase, setPhase] = useState('chat'); // 'chat' | 'workspace'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [turnCount, setTurnCount] = useState(0); // Track conversation turns
  
  // Rotating thinking phrases for loading state
  const { phrase: thinkingPhrase, isVisible: phraseVisible } = useThinkingPhrases(isLoading);
  
  // Attachment state (files)
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draftHash, setDraftHash] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const fileInputRef = useRef(null);
  
  // URL attachment state
  const [urlAttachments, setUrlAttachments] = useState([]); // [{url, crawlMode, status, content}]
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [crawlMode, setCrawlMode] = useState('crawl'); // 'single' | 'crawl'
  const [isCrawling, setIsCrawling] = useState(false);
  const [urlError, setUrlError] = useState(''); // Inline URL validation error
  
  // Canvas state (replaces old productData)
  const [canvas, setCanvas] = useState(null);
  
  // Track items edited in the current turn (for animated border highlight)
  // Format: Set of strings like "product", "persona-p1", "goal-g1", "epic-e1"
  const [editedInTurn, setEditedInTurn] = useState(new Set());
  
  // Draft/Save state
  const [productId, setProductId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Delete confirmation modal
  const autoSaveIntervalRef = useRef(null);
  const canvasScrollRef = useRef(null);
  const loadedDraftRef = useRef(null);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    personas: true,
    goals: true,
    initiatives: true,
    epics: true
  });
  
  // Track which individual epics have expanded feature lists (just titles)
  const [expandedEpicLists, setExpandedEpicLists] = useState(new Set());
  
  // Edit modal state - supports epic with features and tabs, persona with rich fields
  const [editModal, setEditModal] = useState({
    open: false,
    type: null,      // 'product' | 'persona' | 'goal' | 'initiative' | 'epic'
    id: null,        // item id
    title: '',       // name for epic/feature, title for goal
    description: '',
    features: [],    // for epic editing
    activeTab: 'info', // 'info' | 'features' (for epic modal)
    // Rich persona fields
    role: '',
    goals: '',
    pain_points: '',
    context: '',
    // Vision fields for product
    high_level_vision: '',
    target_audience: '',
    value_proposition: '',
    key_differentiators: '',
    // Epic relationship fields
    persona_ids: [],
    goal_ids: [],
    success_criteria: ''
  });
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Toggle expanded feature list for an epic (just bullet list, not full details)
  const toggleEpicListExpand = (epicId) => {
    setExpandedEpicLists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(epicId)) {
        newSet.delete(epicId);
      } else {
        newSet.add(epicId);
      }
      return newSet;
    });
  };

  // Clear highlight when user clicks a card (views/edits)
  const clearItemHighlight = (itemKey) => {
    setEditedInTurn(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemKey);
      return newSet;
    });
  };

  // Open edit modal for an item
  const openEditModal = (type, id, title, description, features = [], extraFields = {}) => {
    // Clear highlight for this item
    const itemKey = type === 'product' ? 'product' : `${type}-${id}`;
    clearItemHighlight(itemKey);
    
    setEditModal({
      open: true,
      type,
      id,
      title: title || '',
      description: description || '',
      features: features || [],
      activeTab: 'info',
      // Rich persona fields
      role: extraFields.role || '',
      goals: extraFields.goals || '',
      pain_points: extraFields.pain_points || '',
      context: extraFields.context || '',
      // Vision fields for product
      high_level_vision: extraFields.high_level_vision || '',
      target_audience: extraFields.target_audience || '',
      value_proposition: extraFields.value_proposition || '',
      key_differentiators: extraFields.key_differentiators || '',
      // Epic relationship fields
      initiative_id: extraFields.initiative_id || '',
      persona_ids: extraFields.persona_ids || [],
      goal_ids: extraFields.goal_ids || [],
      success_criteria: extraFields.success_criteria || ''
    });
  };

  // Delete item from modal
  const handleDeleteFromModal = () => {
    if (!editModal.type || !editModal.id) return;
    
    setCanvas(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      
      switch (editModal.type) {
        case 'persona':
          updated.personas = updated.personas.filter(p => p.id !== editModal.id);
          break;
        case 'goal':
          updated.goals = updated.goals.filter(g => g.id !== editModal.id);
          break;
        case 'initiative':
          updated.initiatives = updated.initiatives.filter(i => i.id !== editModal.id);
          // Also clear initiative_id from any epics that reference this initiative
          updated.epics = updated.epics.map(e => 
            e.initiative_id === editModal.id ? { ...e, initiative_id: '' } : e
          );
          break;
        case 'epic':
          updated.epics = updated.epics.filter(e => e.id !== editModal.id);
          break;
        default:
          break;
      }
      
      return updated;
    });
    
    setShowDeleteConfirm(false);
    setEditModal({ open: false, type: null, id: null, title: '', description: '', features: [], activeTab: 'info', role: '', goals: '', pain_points: '', context: '', high_level_vision: '', target_audience: '', value_proposition: '', key_differentiators: '', goal_ids: [], persona_ids: [], success_criteria: '', initiative_id: '' });
    setHasUnsavedChanges(true);
  };

  // Save edit from modal
  const saveEditModal = () => {
    if (!editModal.type) return;
    
    setCanvas(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      
      switch (editModal.type) {
        case 'product':
          updated.product = {
            ...updated.product,
            name: editModal.title,
            high_level_vision: editModal.high_level_vision,
            target_audience: editModal.target_audience,
            value_proposition: editModal.value_proposition,
            key_differentiators: editModal.key_differentiators
          };
          break;
        case 'persona':
          // Persona uses 'name' per field registry
          updated.personas = updated.personas.map(p =>
            p.id === editModal.id
              ? { 
                  ...p, 
                  name: editModal.title, 
                  role: editModal.role,
                  goals: editModal.goals,
                  pain_points: editModal.pain_points,
                  context: editModal.context
                }
              : p
          );
          break;
        case 'goal':
          updated.goals = updated.goals.map(g =>
            g.id === editModal.id
              ? { 
                  ...g, 
                  title: editModal.title, 
                  description: editModal.description
                }
              : g
          );
          break;
        case 'initiative':
          // Initiative uses 'title' per field registry
          updated.initiatives = updated.initiatives.map(i =>
            i.id === editModal.id
              ? { 
                  ...i, 
                  title: editModal.title, 
                  description: editModal.description,
                  goal_ids: editModal.goal_ids
                }
              : i
          );
          break;
        case 'epic':
          // Epic uses 'title' per field registry
          updated.epics = updated.epics.map(e =>
            e.id === editModal.id
              ? { 
                  ...e, 
                  title: editModal.title, 
                  description: editModal.description,
                  initiative_id: editModal.initiative_id,
                  persona_ids: editModal.persona_ids,
                  goal_ids: editModal.goal_ids,
                  success_criteria: editModal.success_criteria,
                  features: editModal.features 
                }
              : e
          );
          break;
        default:
          break;
      }
      
      return updated;
    });
    
    setEditModal({ 
      open: false, type: null, id: null, title: '', description: '', features: [], activeTab: 'info', 
      role: '', goals: '', pain_points: '', context: '', 
      high_level_vision: '', target_audience: '', value_proposition: '', key_differentiators: '',
      persona_ids: [], goal_ids: [], success_criteria: '', initiative_id: ''
    });
    setHasUnsavedChanges(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal({ 
      open: false, type: null, id: null, title: '', description: '', features: [], activeTab: 'info', 
      role: '', goals: '', pain_points: '', context: '', 
      high_level_vision: '', target_audience: '', value_proposition: '', key_differentiators: '',
      persona_ids: [], goal_ids: [], success_criteria: '', initiative_id: ''
    });
  };

  // Track changes for unsaved indicator
  useEffect(() => {
    if (canvas || chatHistory.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [canvas, chatHistory]);

  // Collapse sidebar when entering workspace mode
  useEffect(() => {
    if (phase === 'workspace') {
      setIsCollapsed(true);
    }
  }, [phase, setIsCollapsed]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load draft from URL param on mount
  const draftIdParam = searchParams.get('draft');
  useEffect(() => {
    if (draftIdParam && loadedDraftRef.current !== draftIdParam) {
      loadedDraftRef.current = draftIdParam;
      loadDraftFromProduct(draftIdParam);
    }
  }, [draftIdParam]);

  // Load draft data from a product ID
  const loadDraftFromProduct = async (productIdParam) => {
    setIsLoadingDraft(true);
    try {
      const response = await productWizardApi.getDraftByProductId(productIdParam);
      const draftData = response.data;
      
      if (draftData) {
        // Set the draft hash and product ID
        setDraftHash(draftData.draft_hash);
        setProductId(draftData.product_id);
        
        // Load canvas if it exists
        if (draftData.canvas) {
          setCanvas(draftData.canvas);
        }
        
        // Load chat history if it exists
        if (draftData.chat_history && draftData.chat_history.length > 0) {
          setChatHistory(draftData.chat_history);
          setTurnCount(draftData.chat_history.filter(m => m.role === 'user').length);
        }
        
        // Load attachments if they exist
        if (draftData.attachments && draftData.attachments.length > 0) {
          setAttachments(draftData.attachments.map(a => ({
            id: a.id || a.attachment_id,
            name: a.name || a.filename,
            type: a.type || a.content_type,
            size: a.size,
            s3_url: a.s3_url,
            uploading: false
          })));
        }
        
        // If we have a canvas, go straight to workspace mode
        if (draftData.canvas) {
          setPhase('workspace');
        }
        
        setHasUnsavedChanges(false);
        toast.success('Draft loaded');
      }
    } catch (err) {
      console.error('Error loading draft:', err);
      toast.error('Failed to load draft');
    } finally {
      setIsLoadingDraft(false);
    }
  };

  // Save draft handler - defined before effects that use it
  const handleSaveDraft = useCallback(async (silent = false) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const response = await productWizardApi.saveDraft({
        draft_hash: draftHash,
        canvas: canvas,
        chat_history: chatHistory.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments?.map(a => ({
            id: a.id,
            name: a.name,
            type: a.type,
            size: a.size,
            s3_url: a.s3_url
          }))
        })),
        attachments: attachments.filter(a => !a.uploading).map(a => ({
          id: a.id,
          name: a.name,
          type: a.type,
          size: a.size,
          s3_key: a.s3_key
        }))
      });
      
      const { product_id, draft_hash, saved_at, is_new } = response.data;
      
      setProductId(product_id);
      setDraftHash(draft_hash);
      setLastSavedAt(new Date(saved_at));
      setHasUnsavedChanges(false);
      
      if (!silent) {
        toast.success(is_new ? 'Draft created' : 'Draft saved');
      }
    } catch (error) {
      console.error('Save draft error:', error);
      if (!silent) {
        toast.error('Failed to save draft');
      }
    } finally {
      setIsSaving(false);
    }
  }, [draftHash, canvas, chatHistory, attachments, isSaving]);

  // Auto-save every 30 seconds after first save
  useEffect(() => {
    if (productId && hasUnsavedChanges) {
      autoSaveIntervalRef.current = setInterval(() => {
        handleSaveDraft(true); // silent auto-save
      }, 30000);
    }
    
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [productId, hasUnsavedChanges, handleSaveDraft]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !productId) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, productId]);

  // Create product handler
  const handleCreateProduct = async () => {
    if (!canvas || !canvas.product?.name) {
      toast.error('Please provide a product name before creating');
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await productWizardApi.createProduct({
        draft_hash: draftHash || `temp_${Date.now()}`,
        canvas: canvas,
        chat_history: chatHistory.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      });
      
      const { product_id, name, personas_created, goals_created, epics_created, features_created } = response.data;
      
      toast.success(`Product "${name}" created successfully!`, {
        description: `${personas_created} personas, ${goals_created} goals, ${epics_created} epics, ${features_created} features`
      });
      
      // Clear auto-save interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
      
      // Navigate to the new product
      navigate(`/products/${product_id}`);
    } catch (error) {
      console.error('Create product error:', error);
      toast.error('Failed to create product');
    } finally {
      setIsCreating(false);
    }
  };

  // Cancel handler with warning
  const handleCancel = () => {
    if (hasUnsavedChanges && !productId) {
      setShowCancelModal(true);
    } else {
      navigate('/products');
    }
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    navigate('/products');
  };

  // File handling - uploads to S3 immediately
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFiles = async (files) => {
    for (const file of files) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      setAttachments(prev => [...prev, {
        id: tempId,
        name: file.name,
        size: file.size,
        type: file.type,
        uploading: true
      }]);
      setUploadingFiles(prev => new Set([...prev, tempId]));
      
      try {
        // Generate a draft hash if we don't have one yet
        let currentDraftHash = draftHash;
        if (!currentDraftHash) {
          currentDraftHash = `draft_${Date.now().toString(36)}${Math.random().toString(36).substr(2, 8)}`;
          setDraftHash(currentDraftHash);
        }

        const { presignedUpload } = await import('../services/upload');
        const result = await presignedUpload(file, {
          organizationId: organization?.organization_id,
          entityType: 'draft',
          entityId: currentDraftHash,
        });
        
        setAttachments(prev => prev.map(a => 
          a.id === tempId 
            ? {
                id: result.file_id,
                name: file.name,
                size: result.size_bytes,
                type: file.type,
                s3_key: result.s3_key,
                s3_url: result.url,
                draft_hash: currentDraftHash,
                uploading: false
              }
            : a
        ));
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
        setAttachments(prev => prev.filter(a => a.id !== tempId));
      } finally {
        setUploadingFiles(prev => {
          const next = new Set(prev);
          next.delete(tempId);
          return next;
        });
      }
    }
  };

  const removeAttachment = async (attachment) => {
    const { id, draft_hash } = attachment;
    
    // If it's still uploading, just remove from state
    if (attachment.uploading) {
      setAttachments(prev => prev.filter(a => a.id !== id));
      return;
    }
    
    // Delete from S3
    const hashToUse = draft_hash || draftHash;
    if (hashToUse && id) {
      try {
        await draftsApi.deleteFile(hashToUse, id);
        toast.success('Attachment removed');
      } catch (error) {
        console.error('Delete failed:', error);
        toast.error('Failed to delete attachment');
        return;
      }
    }
    
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    uploadFiles(files);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check if file is an image
  const isImageFile = (type) => {
    return type && type.startsWith('image/');
  };

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('sheet') || type?.includes('excel') || type?.includes('csv')) return '📊';
    if (type?.includes('presentation') || type?.includes('powerpoint')) return '📽️';
    if (type?.includes('text')) return '📃';
    if (type?.includes('zip') || type?.includes('archive')) return '📦';
    return '📎';
  };

  // URL handling functions
  const isValidUrl = (string) => {
    try {
      const urlString = string.startsWith('http') ? string : 'https://' + string;
      const url = new URL(urlString);
      
      // Must be http or https
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return false;
      }
      
      // Hostname must have at least one dot (e.g., example.com) or be localhost
      const hostname = url.hostname.toLowerCase();
      if (hostname === 'localhost') return true;
      
      // Check for valid domain format (must have at least one dot and valid TLD)
      const parts = hostname.split('.');
      if (parts.length < 2) return false;
      
      // Last part (TLD) must be at least 2 characters
      const tld = parts[parts.length - 1];
      if (tld.length < 2) return false;
      
      // No empty parts
      if (parts.some(part => part.length === 0)) return false;
      
      return true;
    } catch {
      return false;
    }
  };

  const handleAddUrl = () => {
    if (!urlInputValue.trim()) return;
    
    let url = urlInputValue.trim();
    // Add https if no protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    // Validate URL format
    if (!isValidUrl(url)) {
      setUrlError('Please enter a valid URL (e.g., example.com)');
      return;
    }
    
    // Check for duplicates
    if (urlAttachments.some(u => u.url === url)) {
      setUrlError('This URL is already added');
      return;
    }
    
    setUrlError(''); // Clear any previous error
    setUrlAttachments(prev => [...prev, { 
      url, 
      crawlMode, 
      status: 'pending',
      content: null 
    }]);
    setUrlInputValue('');
    setShowUrlInput(false);
    toast.success('URL added');
  };

  const handleRemoveUrl = (url) => {
    setUrlAttachments(prev => prev.filter(u => u.url !== url));
  };

  // Truncate URL for display
  const truncateUrl = (url, maxLength = 30) => {
    try {
      const parsed = new URL(url);
      const display = parsed.hostname + parsed.pathname;
      if (display.length > maxLength) {
        return display.substring(0, maxLength) + '...';
      }
      return display;
    } catch {
      return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
    }
  };

  // Crawl a single URL and return its content
  const crawlUrl = async (urlInfo) => {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000); // 2 min timeout
      
      fetch(`${API_BASE_URL}/api/product-wizard/crawl-url`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: urlInfo.url,
          single_page: urlInfo.crawlMode === 'single'
        }),
        signal: controller.signal,
        credentials: 'include'
      })
        .then(response => {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let result = null;

          const processStream = async () => {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'complete') {
                      result = data;
                    } else if (data.type === 'error') {
                      throw new Error(data.error);
                    }
                  } catch (e) {
                    if (e.message) throw e;
                  }
                }
              }
            }
            
            clearTimeout(timeout);
            if (result) {
              resolve(result);
            } else {
              reject(new Error('Crawl completed but no content returned'));
            }
          };

          processStream().catch(reject);
        })
        .catch(err => {
          clearTimeout(timeout);
          reject(err);
        });
    });
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    
    // Capture current attachments to include with message
    const messageAttachments = attachments.filter(a => !a.uploading);
    // Only include URLs that haven't failed (user can remove failed ones)
    const messageUrls = urlAttachments.filter(u => !u.failed);
    
    // If there are URLs to crawl, do that first before clearing anything
    if (messageUrls.length > 0) {
      setIsLoading(true);
      
      // Track crawl results
      const crawlResults = [];
      let hasFailure = false;
      
      // Add crawling message to chat temporarily
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: `Crawling ${messageUrls.length} URL${messageUrls.length > 1 ? 's' : ''}...`,
        isSystemMessage: true,
        isCrawling: true
      }]);

      for (const urlInfo of messageUrls) {
        try {
          // Update status message
          setChatHistory(prev => {
            const newHistory = [...prev];
            const crawlMsgIndex = newHistory.findIndex(m => m.isCrawling);
            if (crawlMsgIndex !== -1) {
              newHistory[crawlMsgIndex] = {
                ...newHistory[crawlMsgIndex],
                content: `Crawling ${urlInfo.url}...`
              };
            }
            return newHistory;
          });

          const crawlResult = await crawlUrl(urlInfo);
          if (crawlResult && crawlResult.content) {
            crawlResults.push({
              url: urlInfo.url,
              content: crawlResult.content,
              success: true
            });
          } else {
            crawlResults.push({
              url: urlInfo.url,
              error: 'No content extracted',
              success: false
            });
            hasFailure = true;
          }
        } catch (crawlError) {
          console.error(`Failed to crawl ${urlInfo.url}:`, crawlError);
          crawlResults.push({
            url: urlInfo.url,
            error: crawlError.message,
            success: false
          });
          hasFailure = true;
        }
      }

      // Remove the crawling status message
      setChatHistory(prev => prev.filter(m => !m.isCrawling));
      
      // If any URL failed, abort and restore state
      if (hasFailure) {
        setIsLoading(false);
        
        // Mark failed URLs in the urlAttachments state
        setUrlAttachments(prev => prev.map(urlInfo => {
          const result = crawlResults.find(r => r.url === urlInfo.url);
          if (result && !result.success) {
            return { ...urlInfo, failed: true, error: result.error };
          }
          return urlInfo;
        }));
        
        // Show error toast with helpful message
        const failedUrls = crawlResults.filter(r => !r.success);
        toast.error(
          <div className="space-y-2">
            <p className="font-medium">Failed to crawl {failedUrls.length} URL{failedUrls.length > 1 ? 's' : ''}</p>
            <p className="text-sm text-gray-600">The site may use JavaScript rendering or have bot protection.</p>
            <p className="text-sm text-gray-600"><strong>Tip:</strong> Try saving the page as a PDF and attaching it instead.</p>
          </div>,
          { duration: 8000 }
        );
        
        return; // Don't send message to AI
      }
      
      // All URLs crawled successfully - continue with message send
      // Build crawled content string
      let crawledContent = '';
      for (const result of crawlResults) {
        if (result.success) {
          crawledContent += `\n\n=== Content from ${result.url} ===\n`;
          crawledContent += result.content;
        }
      }
      
      // Now clear inputs and proceed
      setMessage('');
      setAttachments([]);
      setUrlAttachments([]);
      
      // Add user message with attachments to chat history
      const userChatMessage = { 
        role: 'user', 
        content: userMessage,
        attachments: messageAttachments,
        urls: messageUrls.map(u => u.url)
      };
      setChatHistory(prev => [...prev, userChatMessage]);
      
      // Add success message
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: `Successfully crawled ${messageUrls.length} URL${messageUrls.length > 1 ? 's' : ''}. Analyzing content...`,
        isSystemMessage: true
      }]);

      // Build the full message with crawled content
      const fullMessage = `${userMessage}\n\n[CRAWLED WEB CONTENT]\n${crawledContent}`;
      
      try {
        // Build request payload
        const requestPayload = {
          message: fullMessage,
          conversation_history: chatHistory.filter(m => !m.isSystemMessage).map(msg => ({
            role: msg.role,
            content: msg.content,
            attachments: msg.attachments?.map(a => ({
              attachment_id: a.id,
              filename: a.name,
              s3_url: a.s3_url,
              content_type: a.type
            })),
            actions: msg.actions
          })),
          canvas_state: canvas,  // Pass current canvas to LLM for context
          attachments: messageAttachments.map(a => ({
            attachment_id: a.id,
            filename: a.name,
            s3_key: a.s3_key,
            s3_url: a.s3_url,
            content_type: a.type,
            size: a.size
          })),
          draft_hash: draftHash
        };

        // Call the AI endpoint
        const response = await productWizardApi.chat(requestPayload);
        const { response: aiMessage, actions, refreshed_attachments } = response.data;

        // Update attachments with refreshed URLs if provided
        if (refreshed_attachments) {
          // Update any attachments in chat history with new URLs
        }

        // Determine if this is the first turn (no existing canvas)
        const isFirstTurn = !canvas || turnCount === 0;

        // Clear previous highlights at start of each turn
        setEditedInTurn(new Set());

        // Apply actions to canvas and track edited items
        // BUT only if there are canvas-modifying actions (not just ASK_CLARIFICATION)
        let shouldTransitionToWorkspace = false;
        const canvasModifyingActions = actions?.filter(a => (a.type || a.action) !== 'ASK_CLARIFICATION') || [];
        
        if (canvasModifyingActions.length > 0) {
          const { canvas: newCanvas, editedItems, shouldTransition } = applyCanvasActions(canvas, canvasModifyingActions, isFirstTurn);
          setCanvas(newCanvas);
          shouldTransitionToWorkspace = shouldTransition;
          
          // Set new edited items for highlighting (only after first turn)
          if (!isFirstTurn && editedItems.size > 0) {
            setEditedInTurn(editedItems);
            
            // Scroll to first edited item after a short delay
            setTimeout(() => {
              const firstEdited = Array.from(editedItems)[0];
              const element = document.querySelector(`[data-item-key="${firstEdited}"]`);
              if (element && canvasScrollRef.current) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        }

        // Increment turn count
        setTurnCount(prev => prev + 1);

        // Add AI response to chat history (filter out system messages)
        setChatHistory(prev => [
          ...prev.filter(m => !m.isSystemMessage),
          { 
            role: 'assistant', 
            content: aiMessage,
            actions: actions
          }
        ]);

        // Only transition to workspace if AI returned canvas-modifying actions
        if (phase === 'chat' && shouldTransitionToWorkspace) {
          setIsTransitioning(true);
          await new Promise(resolve => setTimeout(resolve, 300));
          setPhase('workspace');
          setIsTransitioning(false);
        }

        // Mark that we have unsaved changes
        setHasUnsavedChanges(true);

      } catch (error) {
        console.error('Error in product wizard chat:', error);
        // Show specific error for budget/payment issues, generic for others
        const { showCopilotErrorToast } = await import('../components/copilot/shared/ErrorRecovery');
        showCopilotErrorToast(error);
        // Restore the message on error
        setMessage(userMessage);
        // Remove the user message from chat history
        setChatHistory(prev => prev.filter(m => m !== userChatMessage && !m.isSystemMessage));
      } finally {
        setIsLoading(false);
      }
      
      return; // Exit early since we handled URL case
    }
    
    // No URLs to crawl - proceed with normal message send
    setMessage('');
    setAttachments([]); // Clear attachments after sending
    setUrlAttachments([]); // Clear URL attachments after sending
    
    // Add user message with attachments to chat history
    const userChatMessage = { 
      role: 'user', 
      content: userMessage,
      attachments: messageAttachments,
      urls: []
    };
    setChatHistory(prev => [...prev, userChatMessage]);
    setIsLoading(true);

    try {
      // Build request payload (no URLs in this path)
      const requestPayload = {
        message: userMessage,
        conversation_history: chatHistory.filter(m => !m.isSystemMessage).map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments?.map(a => ({
            attachment_id: a.id,
            filename: a.name,
            s3_url: a.s3_url,
            content_type: a.type
          })),
          actions: msg.actions
        })),
        canvas_state: canvas,  // Pass current canvas to LLM for context
        attachments: messageAttachments.map(a => ({
          attachment_id: a.id,
          filename: a.name,
          s3_key: a.s3_key,
          s3_url: a.s3_url,
          content_type: a.type,
          size: a.size
        })),
        draft_hash: draftHash
      };

      // Call the AI endpoint
      const response = await productWizardApi.chat(requestPayload);
      const { response: aiMessage, actions, refreshed_attachments } = response.data;

      // Update attachments with refreshed URLs if provided
      if (refreshed_attachments) {
        // Update any attachments in chat history with new URLs
      }

      // Determine if this is the first turn (no existing canvas)
      const isFirstTurn = !canvas || turnCount === 0;

      // Clear previous highlights at start of each turn
      setEditedInTurn(new Set());

      // Apply actions to canvas and track edited items
      // BUT only if there are canvas-modifying actions (not just ASK_CLARIFICATION)
      let shouldTransitionToWorkspace = false;
      const canvasModifyingActions = actions?.filter(a => (a.type || a.action) !== 'ASK_CLARIFICATION') || [];
      
      if (canvasModifyingActions.length > 0) {
        const { canvas: newCanvas, editedItems, shouldTransition } = applyCanvasActions(canvas, canvasModifyingActions, isFirstTurn);
        setCanvas(newCanvas);
        shouldTransitionToWorkspace = shouldTransition;
        
        // Set new edited items for highlighting (only after first turn)
        if (!isFirstTurn && editedItems.size > 0) {
          setEditedInTurn(editedItems);
          
          // Scroll to first edited item after a short delay
          setTimeout(() => {
            const firstEdited = Array.from(editedItems)[0];
            const element = document.querySelector(`[data-item-key="${firstEdited}"]`);
            if (element && canvasScrollRef.current) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      }

      // Increment turn count
      setTurnCount(prev => prev + 1);

      // Add AI response to chat history (filter out system messages)
      setChatHistory(prev => [
        ...prev.filter(m => !m.isSystemMessage),
        { 
          role: 'assistant', 
          content: aiMessage,
          actions: actions
        }
      ]);

      // Only transition to workspace if AI returned canvas-modifying actions
      if (phase === 'chat' && shouldTransitionToWorkspace) {
        setIsTransitioning(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        setPhase('workspace');
        setIsTransitioning(false);
      }

      // Mark that we have unsaved changes
      setHasUnsavedChanges(true);

    } catch (error) {
      console.error('AI chat error:', error);
      const { showCopilotErrorToast } = await import('../components/copilot/shared/ErrorRecovery');
      showCopilotErrorToast(error);
      // Restore message on error
      setMessage(userMessage);
      // Remove the user message we optimistically added
      setChatHistory(prev => prev.filter(m => m !== userChatMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Chat Phase UI
  const renderChatPhase = () => (
    <div className={cn(
      "flex-1 flex flex-col items-center justify-center p-8 transition-all duration-300",
      isTransitioning && "opacity-0 translate-x-[-50px]"
    )}>
      {/* Header / Instructions */}
      <div className="w-full max-w-3xl mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 mb-4">
          <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">AI-Powered Product Creation</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          Describe your product idea
        </h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
          {isFreeProduct 
            ? "Tell me about what you're building. I'll help you define epics and features to get started."
            : "Tell me about what you're building. I'll help you define personas, goals, initiatives, and features to get started."
          }
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-4">
          Or{' '}
          <Link to="/products/new-simple" className="text-blue-500 hover:underline">
            create a product from scratch
          </Link>{' '}
          using a simple form.
        </p>
      </div>

      {/* Chat Container */}
      <div className="w-full max-w-3xl flex-1 flex flex-col min-h-0">
        {/* Messages */}
        <ScrollArea className="flex-1 mb-4">
          <div className="space-y-4 pr-4">
            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex",
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-3",
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <CopilotMarkdown 
                      content={msg.content}
                      enableEntityLinks={false}
                      className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mb-3 [&>ol]:mb-3 [&>ul]:pl-4 [&>ol]:pl-4 [&>li]:mb-1 [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-2"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  )}
                  
                  {/* Message Attachments */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.attachments.map((att) => (
                        <a
                          key={att.id}
                          href={att.s3_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "group relative block rounded-lg overflow-hidden",
                            "border border-white/20 hover:border-white/40 transition-all",
                            isImageFile(att.type) ? "w-24 h-24" : "p-2"
                          )}
                        >
                          {isImageFile(att.type) ? (
                            <>
                              <img 
                                src={att.s3_url} 
                                alt={att.name}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Download className="w-5 h-5 text-white" />
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getFileIcon(att.type)}</span>
                              <div className="flex-1 min-w-0">
                                <p className={cn(
                                  "text-xs font-medium truncate max-w-[100px]",
                                  msg.role === 'user' ? 'text-white/90' : 'text-gray-700 dark:text-gray-300'
                                )}>
                                  {att.name}
                                </p>
                                <p className={cn(
                                  "text-xs",
                                  msg.role === 'user' ? 'text-white/60' : 'text-gray-400'
                                )}>
                                  {formatFileSize(att.size)}
                                </p>
                              </div>
                              <Download className={cn(
                                "w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity",
                                msg.role === 'user' ? 'text-white' : 'text-gray-500'
                              )} />
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start" data-testid="wizard-thinking-indicator">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span 
                      className={cn(
                        "text-sm italic text-gray-500 transition-opacity duration-200",
                        phraseVisible ? "opacity-100" : "opacity-0"
                      )}
                    >
                      {thinkingPhrase}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area with Drop Zone */}
        <div 
          className={cn(
            "relative rounded-xl border-2 transition-colors",
            isDragging 
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
              : "border-gray-200 dark:border-gray-700",
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Attachments Preview (Files + URLs) */}
          {(attachments.length > 0 || urlAttachments.length > 0) && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
              {/* File Attachments */}
              {attachments.map((attachment) => (
                <div 
                  key={attachment.id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm",
                    attachment.uploading 
                      ? "bg-blue-50 dark:bg-blue-900/20" 
                      : "bg-gray-100 dark:bg-gray-800"
                  )}
                >
                  {attachment.uploading ? (
                    <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                  ) : (
                    <Paperclip className="w-3.5 h-3.5 text-gray-500" />
                  )}
                  <span className="text-gray-700 dark:text-gray-300 max-w-[150px] truncate">
                    {attachment.name}
                  </span>
                  <span className="text-gray-400 text-xs">
                    {formatFileSize(attachment.size)}
                  </span>
                  <button 
                    onClick={() => removeAttachment(attachment)}
                    disabled={attachment.uploading}
                    className={cn(
                      "p-0.5 rounded",
                      attachment.uploading 
                        ? "opacity-50 cursor-not-allowed" 
                        : "hover:bg-gray-200 dark:hover:bg-gray-700"
                    )}
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </div>
              ))}
              {/* URL Attachments */}
              {urlAttachments.map((urlInfo) => (
                <div 
                  key={urlInfo.url}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm",
                    urlInfo.failed 
                      ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                      : "bg-green-50 dark:bg-green-900/20"
                  )}
                  title={urlInfo.failed ? `Failed: ${urlInfo.error}` : urlInfo.url}
                >
                  <Globe className={cn(
                    "w-3.5 h-3.5",
                    urlInfo.failed ? "text-red-500" : "text-green-600"
                  )} />
                  <span className={cn(
                    "max-w-[180px] truncate",
                    urlInfo.failed 
                      ? "text-red-600 dark:text-red-400 line-through" 
                      : "text-gray-700 dark:text-gray-300"
                  )}>
                    {truncateUrl(urlInfo.url)}
                  </span>
                  {urlInfo.failed ? (
                    <span className="text-red-500 text-xs font-medium">Failed</span>
                  ) : (
                    <span className="text-green-600 text-xs">
                      {urlInfo.crawlMode === 'single' ? 'Single' : 'Crawl'}
                    </span>
                  )}
                  <button 
                    onClick={() => handleRemoveUrl(urlInfo.url)}
                    className={cn(
                      "p-0.5 rounded",
                      urlInfo.failed 
                        ? "hover:bg-red-100 dark:hover:bg-red-900/40"
                        : "hover:bg-green-100 dark:hover:bg-green-900/40"
                    )}
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your product idea... What problem does it solve? Who is it for?"
            className={cn(
              "w-full min-h-[180px] resize-none border-0 shadow-none",
              "text-base placeholder:text-gray-400",
              "rounded-t-none focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            )}
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          
          {/* Bottom toolbar */}
          <div className="flex items-center justify-between p-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.json,.html,.htm,.xml,.yaml,.yml"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                data-testid="attach-files-btn"
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Attach files
              </Button>
              
              {/* URL Attachment Button - hidden for now */}
              {false && <Popover open={showUrlInput} onOpenChange={(open) => {
                setShowUrlInput(open);
                if (!open) setUrlError(''); // Clear error when closing
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                    data-testid="attach-url-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    URL
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[480px]" align="start">
                  <div className="space-y-3">
                    <div className="flex gap-3 items-end">
                      <div className="flex-1 space-y-1.5">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Website URL
                        </label>
                        <Input
                          value={urlInputValue}
                          onChange={(e) => {
                            setUrlInputValue(e.target.value);
                            if (urlError) setUrlError(''); // Clear error on typing
                          }}
                          placeholder="https://example.com"
                          className={cn("text-sm", urlError && "border-red-500 focus-visible:ring-red-500")}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddUrl();
                            }
                          }}
                        />
                        {urlError && (
                          <p className="text-xs text-red-500">{urlError}</p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Mode
                        </label>
                        <div className="flex gap-1">
                          <Button
                            variant={crawlMode === 'single' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCrawlMode('single')}
                          >
                            Single page
                          </Button>
                          <Button
                            variant={crawlMode === 'crawl' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCrawlMode('crawl')}
                          >
                            Crawl site
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {crawlMode === 'single' 
                          ? 'Fetch only the specified page' 
                          : 'Follow links (up to 10 pages, depth 2)'}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowUrlInput(false);
                            setUrlInputValue('');
                            setUrlError('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddUrl}
                          disabled={!urlInputValue.trim()}
                        >
                          Add URL
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>}

              {isDragging && (
                <span className="text-sm text-blue-600">Drop files here...</span>
              )}
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || isLoading || uploadingFiles.size > 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-message-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Workspace Phase UI
  const renderWorkspacePhase = () => (
    <div className={cn(
      "flex-1 flex transition-all duration-300 h-full overflow-hidden",
      isTransitioning && "opacity-0"
    )}>
      {/* Chat Panel - Left Side (~50%) - Fixed in viewport */}
      <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full bg-white dark:bg-gray-900">
        {/* Chat Header */}
        <div className="shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-gray-900 dark:text-white">Product Assistant</h2>
          </div>
        </div>

        {/* Chat Messages - Scrollable within fixed height */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {chatHistory.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex",
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    "max-w-[90%] rounded-2xl px-4 py-3",
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm'
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <CopilotMarkdown 
                      content={msg.content}
                      enableEntityLinks={false}
                      className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mb-3 [&>ol]:mb-3 [&>ul]:pl-4 [&>ol]:pl-4 [&>li]:mb-1 [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-2"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  )}
                  
                  {/* Message Attachments */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {msg.attachments.map((att) => (
                        <a
                          key={att.id}
                          href={att.s3_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "group relative block rounded-lg overflow-hidden",
                            "border border-white/20 hover:border-white/40 transition-all",
                            isImageFile(att.type) ? "w-16 h-16" : "p-1.5"
                          )}
                        >
                          {isImageFile(att.type) ? (
                            <>
                              <img 
                                src={att.s3_url} 
                                alt={att.name}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Download className="w-4 h-4 text-white" />
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm">{getFileIcon(att.type)}</span>
                              <span className={cn(
                                "text-xs truncate max-w-[80px]",
                                msg.role === 'user' ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'
                              )}>
                                {att.name}
                              </span>
                              <Download className={cn(
                                "w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity",
                                msg.role === 'user' ? 'text-white' : 'text-gray-500'
                              )} />
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start" data-testid="wizard-workspace-thinking">
                <div className="bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span 
                      className={cn(
                        "text-sm italic text-gray-500 transition-opacity duration-200",
                        phraseVisible ? "opacity-100" : "opacity-0"
                      )}
                    >
                      {thinkingPhrase}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>

        {/* Chat Input - Fixed at bottom - Matching starting page style */}
        <div className="shrink-0 p-4 bg-white dark:bg-gray-900">
          <div 
            className={cn(
              "rounded-xl border-2 transition-colors",
              isDragging 
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
                : "border-gray-200 dark:border-gray-700",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Attachments Preview (Files + URLs) */}
            {(attachments.length > 0 || urlAttachments.length > 0) && (
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                {/* File Attachments */}
                {attachments.map((attachment) => (
                  <div 
                    key={attachment.id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm",
                      attachment.uploading 
                        ? "bg-blue-50 dark:bg-blue-900/20" 
                        : "bg-gray-100 dark:bg-gray-800"
                    )}
                  >
                    {attachment.uploading ? (
                      <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                    ) : (
                      <Paperclip className="w-3.5 h-3.5 text-gray-500" />
                    )}
                    <span className="text-gray-700 dark:text-gray-300 max-w-[100px] truncate">
                      {attachment.name}
                    </span>
                    <button 
                      onClick={() => removeAttachment(attachment)}
                      disabled={attachment.uploading}
                      className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
                {/* URL Attachments */}
                {urlAttachments.map((urlInfo) => (
                  <div 
                    key={urlInfo.url}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm",
                      urlInfo.failed 
                        ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                        : "bg-green-50 dark:bg-green-900/20"
                    )}
                    title={urlInfo.failed ? `Failed: ${urlInfo.error}` : urlInfo.url}
                  >
                    <Globe className={cn(
                      "w-3.5 h-3.5",
                      urlInfo.failed ? "text-red-500" : "text-green-600"
                    )} />
                    <span className={cn(
                      "max-w-[100px] truncate",
                      urlInfo.failed 
                        ? "text-red-600 dark:text-red-400 line-through" 
                        : "text-gray-700 dark:text-gray-300"
                    )}>
                      {truncateUrl(urlInfo.url, 20)}
                    </span>
                    {urlInfo.failed && (
                      <span className="text-red-500 text-xs font-medium">Failed</span>
                    )}
                    <button 
                      onClick={() => handleRemoveUrl(urlInfo.url)}
                      className={cn(
                        "p-0.5 rounded",
                        urlInfo.failed 
                          ? "hover:bg-red-100 dark:hover:bg-red-900/40"
                          : "hover:bg-green-100 dark:hover:bg-green-900/40"
                      )}
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ask me to refine any section..."
              className={cn(
                "w-full min-h-[100px] resize-none border-0 shadow-none",
                "text-sm placeholder:text-gray-400",
                "focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              )}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            
            {/* Bottom toolbar */}
            <div className="flex items-center justify-between p-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  id="workspace-file-input"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.json,.html,.htm,.xml,.yaml,.yml"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => document.getElementById('workspace-file-input')?.click()}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  disabled={isLoading}
                >
                  <Paperclip className="w-4 h-4 mr-1" />
                  Attach
                </Button>
                
                {/* URL Attachment Button - hidden for now */}
                {false && <Popover open={showUrlInput} onOpenChange={(open) => {
                  setShowUrlInput(open);
                  if (!open) setUrlError(''); // Clear error when closing
                }}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      disabled={isLoading}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      URL
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[480px]" align="start">
                    <div className="space-y-3">
                      <div className="flex gap-3 items-end">
                        <div className="flex-1 space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Website URL
                          </label>
                          <Input
                            value={urlInputValue}
                            onChange={(e) => {
                              setUrlInputValue(e.target.value);
                              if (urlError) setUrlError(''); // Clear error on typing
                            }}
                            placeholder="https://example.com"
                            className={cn("text-sm", urlError && "border-red-500 focus-visible:ring-red-500")}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddUrl();
                              }
                            }}
                          />
                          {urlError && (
                            <p className="text-xs text-red-500">{urlError}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Mode
                          </label>
                          <div className="flex gap-1">
                            <Button
                              variant={crawlMode === 'single' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCrawlMode('single')}
                            >
                              Single page
                            </Button>
                            <Button
                              variant={crawlMode === 'crawl' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCrawlMode('crawl')}
                            >
                              Crawl site
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          {crawlMode === 'single' 
                            ? 'Fetch only the specified page' 
                            : 'Follow links (up to 10 pages, depth 2)'}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowUrlInput(false);
                              setUrlInputValue('');
                              setUrlError('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleAddUrl}
                            disabled={!urlInputValue.trim()}
                          >
                            Add URL
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>}
              </div>
              <Button
                onClick={handleSendMessage}
                disabled={!message.trim() || isLoading || uploadingFiles.size > 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas - Right Side (~50%) - Independently scrollable */}
      <div className="w-1/2 flex flex-col h-full bg-white dark:bg-gray-900">
        {/* Canvas Header - Fixed */}
        <div className="shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">Product Canvas</h2>
        </div>

        {/* Scrollable Canvas Content */}
        <div className="flex-1 overflow-y-auto" ref={canvasScrollRef}>
          <div className="p-6 space-y-8">
            {canvas && (
              <>
                {/* Product Name & Description */}
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Product Overview
                    </h3>
                  </div>
                  <div className="relative" data-item-key="product">
                    {editedInTurn.has('product') && <AnimatedHighlightBorder />}
                    <Card 
                      className={cn(
                        "p-4 cursor-pointer hover:shadow-md transition-all",
                        editedInTurn.has('product') 
                          ? "border-blue-400 dark:border-blue-500" 
                          : "hover:border-blue-300 dark:hover:border-blue-600"
                      )}
                      onClick={() => openEditModal('product', null, canvas.product?.name, '', [], {
                        high_level_vision: canvas.product?.high_level_vision,
                        target_audience: canvas.product?.target_audience,
                        value_proposition: canvas.product?.value_proposition,
                        key_differentiators: canvas.product?.key_differentiators
                      })}
                      data-testid="product-overview-card"
                    >
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {canvas.product?.name || 'Untitled Product'}
                      </h4>
                      {canvas.product?.high_level_vision && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vision</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                            {canvas.product.high_level_vision}
                          </p>
                        </div>
                      )}
                      {canvas.product?.target_audience && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Target Audience</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                            {canvas.product.target_audience}
                          </p>
                        </div>
                      )}
                    </Card>
                  </div>
                </section>

                {/* Personas - hidden for free tier */}
                {!isFreeProduct && (
                <section>
                  <button
                    onClick={() => toggleSection('personas')}
                    className="flex items-center gap-2 mb-3 w-full text-left"
                  >
                    {expandedSections.personas ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Users className="w-5 h-5 text-purple-500" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Personas
                    </h3>
                    <Badge variant="secondary" className="ml-auto">
                      {canvas.personas?.length || 0}
                    </Badge>
                  </button>
                  {expandedSections.personas && canvas.personas && (
                    <div className="space-y-3">
                      {canvas.personas.map((persona) => {
                        const itemKey = `persona-${persona.id}`;
                        const isHighlighted = editedInTurn.has(itemKey);
                        return (
                          <div className="relative" key={persona.id} data-item-key={itemKey}>
                            {isHighlighted && <AnimatedHighlightBorder />}
                            <Card 
                              className={cn(
                                "p-4 cursor-pointer hover:shadow-md transition-all",
                                isHighlighted 
                                  ? "border-purple-400 dark:border-purple-500" 
                                  : "hover:border-purple-300 dark:hover:border-purple-600"
                              )}
                              onClick={() => openEditModal('persona', persona.id, persona.name, '', [], {
                                role: persona.role,
                                goals: persona.goals,
                                pain_points: persona.pain_points,
                                context: persona.context
                              })}
                              data-testid={`persona-card-${persona.id}`}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {persona.name}
                              </h4>
                              {persona.role && (
                                <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                                  {persona.role}
                                </p>
                              )}
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                                {persona.description}
                              </p>
                              {persona.goals && (
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Goals:</span> {persona.goals.substring(0, 80)}{persona.goals.length > 80 ? '...' : ''}
                                </div>
                              )}
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
                )}

                {/* Goals - hidden for free tier */}
                {!isFreeProduct && (
                <section>
                  <button
                    onClick={() => toggleSection('goals')}
                    className="flex items-center gap-2 mb-3 w-full text-left"
                  >
                    {expandedSections.goals ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Target className="w-5 h-5 text-green-500" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Goals
                    </h3>
                    <Badge variant="secondary" className="ml-auto">
                      {canvas.goals?.length || 0}
                    </Badge>
                  </button>
                  {expandedSections.goals && canvas.goals && (
                    <div className="space-y-3">
                      {canvas.goals.map((goal) => {
                        const itemKey = `goal-${goal.id}`;
                        const isHighlighted = editedInTurn.has(itemKey);
                        return (
                          <div className="relative" key={goal.id} data-item-key={itemKey}>
                            {isHighlighted && <AnimatedHighlightBorder />}
                            <Card 
                              className={cn(
                                "p-4 cursor-pointer hover:shadow-md transition-all",
                                isHighlighted 
                                  ? "border-green-400 dark:border-green-500" 
                                  : "hover:border-green-300 dark:hover:border-green-600"
                              )}
                              onClick={() => openEditModal('goal', goal.id, goal.title, goal.description)}
                              data-testid={`goal-card-${goal.id}`}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {goal.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {goal.description}
                              </p>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
                )}

                {/* Initiatives - hidden for free tier */}
                {!isFreeProduct && (
                <section>
                  <button
                    onClick={() => toggleSection('initiatives')}
                    className="flex items-center gap-2 mb-3 w-full text-left"
                  >
                    {expandedSections.initiatives ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Rocket className="w-5 h-5 text-purple-500" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Initiatives
                    </h3>
                    <Badge variant="secondary" className="ml-auto">
                      {canvas.initiatives?.length || 0}
                    </Badge>
                  </button>
                  {expandedSections.initiatives && canvas.initiatives && (
                    <div className="space-y-3">
                      {canvas.initiatives.map((initiative) => {
                        const itemKey = `initiative-${initiative.id}`;
                        const isHighlighted = editedInTurn.has(itemKey);
                        // Resolve goal names for display
                        const linkedGoals = (initiative.goal_ids || [])
                          .map(gid => canvas.goals?.find(g => g.id === gid)?.title)
                          .filter(Boolean);
                        return (
                          <div className="relative" key={initiative.id} data-item-key={itemKey}>
                            {isHighlighted && <AnimatedHighlightBorder />}
                            <Card 
                              className={cn(
                                "p-4 cursor-pointer hover:shadow-md transition-all",
                                isHighlighted 
                                  ? "border-purple-400 dark:border-purple-500" 
                                  : "hover:border-purple-300 dark:hover:border-purple-600"
                              )}
                              onClick={() => openEditModal('initiative', initiative.id, initiative.title, initiative.description, [], {
                                goal_ids: initiative.goal_ids || []
                              })}
                              data-testid={`initiative-card-${initiative.id}`}
                            >
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {initiative.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {initiative.description}
                              </p>
                              {linkedGoals.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {linkedGoals.map((goalTitle, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                                      {goalTitle}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
                )}

                {/* Epics with Features */}
                <section>
                  <button
                    onClick={() => toggleSection('epics')}
                    className="flex items-center gap-2 mb-3 w-full text-left"
                  >
                    {expandedSections.epics ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Zap className="w-5 h-5 text-amber-500" />
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Epics
                    </h3>
                    <Badge variant="secondary" className="ml-auto">
                      {canvas.epics?.length || 0} epics, {canvas.epics?.reduce((sum, e) => sum + (e.features?.length || 0), 0) || 0} features
                    </Badge>
                  </button>
                  {expandedSections.epics && canvas.epics && (
                    <div className="space-y-3">
                      {canvas.epics.map((epic) => {
                        const itemKey = `epic-${epic.id}`;
                        const isHighlighted = editedInTurn.has(itemKey);
                        const isListExpanded = expandedEpicLists.has(epic.id);
                        const featuresCount = epic.features?.length || 0;
                        
                        return (
                          <div className="relative" key={epic.id} data-item-key={itemKey}>
                            {isHighlighted && <AnimatedHighlightBorder />}
                            <Card 
                              className={cn(
                                "overflow-hidden cursor-pointer hover:shadow-md transition-all",
                                isHighlighted 
                                  ? "border-amber-400 dark:border-amber-500" 
                                  : "border-amber-200 dark:border-amber-800 hover:border-amber-300 dark:hover:border-amber-600"
                              )}
                              onClick={() => openEditModal('epic', epic.id, epic.title, epic.description, epic.features, {
                                initiative_id: epic.initiative_id,
                                persona_ids: epic.persona_ids,
                                goal_ids: epic.goal_ids,
                                success_criteria: epic.success_criteria
                              })}
                              data-testid={`epic-card-${epic.id}`}
                            >
                              {/* Epic Card Content */}
                              <div className="p-4 bg-amber-50 dark:bg-amber-900/20">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0 mt-0.5">
                                    <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold text-gray-900 dark:text-white">
                                        {epic.title}
                                      </h4>
                                      <Badge variant="outline" className="text-xs">
                                        {featuresCount} feature{featuresCount !== 1 ? 's' : ''}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                                      {epic.description}
                                    </p>
                                    
                                    {/* Feature titles list */}
                                    {featuresCount > 0 && (
                                      <ul className="mt-3 space-y-1">
                                        {epic.features.slice(0, isListExpanded ? undefined : 4).map((feature, idx) => (
                                          <li key={feature.id || idx} className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                                            {feature.title}
                                          </li>
                                        ))}
                                        {featuresCount > 4 && !isListExpanded && (
                                          <li 
                                            className="text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleEpicListExpand(epic.id);
                                            }}
                                          >
                                            + {featuresCount - 4} more...
                                          </li>
                                        )}
                                        {isListExpanded && featuresCount > 4 && (
                                          <li 
                                            className="text-sm text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleEpicListExpand(epic.id);
                                            }}
                                          >
                                            Show less
                                          </li>
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>

        {/* Canvas Footer - Fixed */}
        <div className="shrink-0 px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            {lastSavedAt && (
              <span className="text-xs text-gray-500">
                Last saved: {new Date(lastSavedAt).toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              data-testid="cancel-wizard-btn"
            >
              Cancel
            </Button>
            {phase === 'workspace' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSaveDraft(false)}
                  disabled={isSaving || isCreating}
                  data-testid="save-draft-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save as Draft
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleCreateProduct}
                  disabled={isCreating || isSaving || !canvas?.product?.name}
                  data-testid="create-product-btn"
                >
                  {isCreating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Create Product
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Loading state when fetching draft
  if (isLoadingDraft) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading draft...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className={cn(
          "shrink-0 px-6 py-3 border-b flex items-center justify-between",
          "bg-white dark:bg-gray-900",
          "border-gray-200 dark:border-gray-700"
        )}>
          <div className="flex items-center gap-3">
            {isFreeProduct && <FreeProductAvatarMenu />}
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {draftHash ? 'Edit Draft' : 'New Product'}
            </h1>
            {phase === 'workspace' && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-900/20">
                Draft
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
          </div>
        </div>
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {phase === 'chat' ? renderChatPhase() : renderWorkspacePhase()}
        </div>
      </div>
      
      {/* Cancel Warning Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes that will be lost if you leave. Would you like to save your progress as a draft before leaving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
            >
              Keep Editing
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                await handleSaveDraft(false);
                setShowCancelModal(false);
                navigate('/products');
              }}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save & Leave
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancel}
            >
              Discard & Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Item Modal - Large size to match FeatureDetailModal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && closeEditModal()}>
        <DialogContent className={cn(
          "sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col",
          editModal.type === 'epic' && "sm:max-w-6xl"
        )}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl">
                Edit {editModal.type === 'product' ? 'Product' : 
                      editModal.type === 'persona' ? 'Persona' :
                      editModal.type === 'goal' ? 'Goal' : 
                      editModal.type === 'initiative' ? 'Initiative' :
                      editModal.type === 'epic' ? 'Epic' : 'Item'}
              </DialogTitle>
              {/* Delete button for deletable items (not product) */}
              {editModal.type && editModal.type !== 'product' && editModal.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid="delete-item-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {/* Epic uses tabs, others use simple form */}
          {editModal.type === 'epic' ? (
            <Tabs value={editModal.activeTab} onValueChange={(tab) => setEditModal(prev => ({ ...prev, activeTab: tab }))} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Epic Info</TabsTrigger>
                <TabsTrigger value="features">Features ({editModal.features?.length || 0})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 py-4 flex-1 overflow-y-auto">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Epic Name
                  </label>
                  <Input
                    value={editModal.title}
                    onChange={(e) => setEditModal(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter epic name"
                    data-testid="edit-modal-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <Textarea
                    value={editModal.description}
                    onChange={(e) => setEditModal(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter epic description"
                    className="min-h-[80px]"
                    data-testid="edit-modal-description-input"
                  />
                </div>
                
                {/* Parent Initiative selector */}
                {canvas?.initiatives?.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Parent Initiative
                    </label>
                    <select
                      value={editModal.initiative_id || ''}
                      onChange={(e) => setEditModal(prev => ({ ...prev, initiative_id: e.target.value }))}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="edit-modal-initiative-select"
                    >
                      <option value="">Not linked</option>
                      {canvas.initiatives.map(initiative => (
                        <option key={initiative.id} value={initiative.id}>
                          {initiative.title}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">Optionally link this epic to an initiative</p>
                  </div>
                )}
                
                {/* Personas multi-select */}
                {canvas?.personas?.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Target Personas
                    </label>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2 max-h-[120px] overflow-y-auto">
                      {canvas.personas.map(persona => (
                        <label key={persona.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editModal.persona_ids?.includes(persona.id)}
                            onCheckedChange={(checked) => {
                              setEditModal(prev => ({
                                ...prev,
                                persona_ids: checked
                                  ? [...(prev.persona_ids || []), persona.id]
                                  : (prev.persona_ids || []).filter(id => id !== persona.id)
                              }));
                            }}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{persona.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">Select personas this epic serves</p>
                  </div>
                )}
                
                {/* Goals multi-select */}
                {canvas?.goals?.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Linked Goals
                    </label>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2 max-h-[120px] overflow-y-auto">
                      {canvas.goals.map(goal => (
                        <label key={goal.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editModal.goal_ids?.includes(goal.id)}
                            onCheckedChange={(checked) => {
                              setEditModal(prev => ({
                                ...prev,
                                goal_ids: checked
                                  ? [...(prev.goal_ids || []), goal.id]
                                  : (prev.goal_ids || []).filter(id => id !== goal.id)
                              }));
                            }}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{goal.title}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">Select goals this epic supports</p>
                  </div>
                )}
                
                {/* Success Criteria */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Success Criteria
                  </label>
                  <Textarea
                    value={editModal.success_criteria}
                    onChange={(e) => setEditModal(prev => ({ ...prev, success_criteria: e.target.value }))}
                    placeholder="How will we know this epic is successful?"
                    className="min-h-[60px]"
                    data-testid="edit-modal-success-criteria-input"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="features" className="py-4 flex-1 overflow-hidden flex flex-col">
                <div className="space-y-3 flex-1 flex flex-col overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {editModal.features?.length || 0} feature{(editModal.features?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditModal(prev => ({
                          ...prev,
                          features: [...(prev.features || []), { id: `f_new_${Date.now()}`, title: '', description: '', priority: 'should_have', user_story: '', acceptance_criteria: '', persona_id: '', goal_id: '' }]
                        }));
                      }}
                      data-testid="add-feature-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Feature
                    </Button>
                  </div>
                  <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                    {editModal.features?.map((feature, idx) => (
                      <div key={feature.id || idx} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3 bg-gray-50 dark:bg-gray-800">
                        {/* Title and Delete */}
                        <div className="flex items-center gap-2">
                          <Input
                            value={feature.title}
                            onChange={(e) => {
                              const newFeatures = [...editModal.features];
                              newFeatures[idx] = { ...newFeatures[idx], title: e.target.value };
                              setEditModal(prev => ({ ...prev, features: newFeatures }));
                            }}
                            placeholder="Feature title"
                            className="flex-1"
                            data-testid={`feature-title-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newFeatures = editModal.features.filter((_, i) => i !== idx);
                              setEditModal(prev => ({ ...prev, features: newFeatures }));
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Description */}
                        <Textarea
                          value={feature.description}
                          onChange={(e) => {
                            const newFeatures = [...editModal.features];
                            newFeatures[idx] = { ...newFeatures[idx], description: e.target.value };
                            setEditModal(prev => ({ ...prev, features: newFeatures }));
                          }}
                          placeholder="Feature description"
                          className="min-h-[50px] text-sm"
                          data-testid={`feature-desc-${idx}`}
                        />
                        
                        {/* Priority and Persona/Goal dropdowns row */}
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Priority</label>
                            <Select
                              value={feature.priority || 'should_have'}
                              onValueChange={(value) => {
                                const newFeatures = [...editModal.features];
                                newFeatures[idx] = { ...newFeatures[idx], priority: value };
                                setEditModal(prev => ({ ...prev, features: newFeatures }));
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="must_have">Must Have</SelectItem>
                                <SelectItem value="should_have">Should Have</SelectItem>
                                <SelectItem value="nice_to_have">Nice to Have</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {canvas?.personas?.length > 0 && (
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Persona</label>
                              <Select
                                value={feature.persona_id || '_none'}
                                onValueChange={(value) => {
                                  const newFeatures = [...editModal.features];
                                  newFeatures[idx] = { ...newFeatures[idx], persona_id: value === '_none' ? '' : value };
                                  setEditModal(prev => ({ ...prev, features: newFeatures }));
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  {canvas.personas.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          {canvas?.goals?.length > 0 && (
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Goal</label>
                              <Select
                                value={feature.goal_id || '_none'}
                                onValueChange={(value) => {
                                  const newFeatures = [...editModal.features];
                                  newFeatures[idx] = { ...newFeatures[idx], goal_id: value === '_none' ? '' : value };
                                  setEditModal(prev => ({ ...prev, features: newFeatures }));
                                }}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  {canvas.goals.map(g => (
                                    <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        
                        {/* User Story */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">User Story</label>
                          <Textarea
                            value={feature.user_story || ''}
                            onChange={(e) => {
                              const newFeatures = [...editModal.features];
                              newFeatures[idx] = { ...newFeatures[idx], user_story: e.target.value };
                              setEditModal(prev => ({ ...prev, features: newFeatures }));
                            }}
                            placeholder="As a [persona], I want [X] so that [Y]"
                            className="min-h-[40px] text-sm"
                            data-testid={`feature-user-story-${idx}`}
                          />
                        </div>
                        
                        {/* Acceptance Criteria */}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Acceptance Criteria</label>
                          <Textarea
                            value={feature.acceptance_criteria || ''}
                            onChange={(e) => {
                              const newFeatures = [...editModal.features];
                              newFeatures[idx] = { ...newFeatures[idx], acceptance_criteria: e.target.value };
                              setEditModal(prev => ({ ...prev, features: newFeatures }));
                            }}
                            placeholder="Definition of done - what must be true for this feature to be complete"
                            className="min-h-[40px] text-sm"
                            data-testid={`feature-acceptance-${idx}`}
                          />
                        </div>
                      </div>
                    ))}
                    {(!editModal.features || editModal.features.length === 0) && (
                      <div className="text-center text-sm text-gray-500 py-8 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        No features yet. Click &quot;Add Feature&quot; to create one.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            /* Non-epic items: simple form */
            <div className="space-y-4 py-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {editModal.type === 'product' ? 'Product Name' : editModal.type === 'persona' ? 'Persona Name' : 'Title'}
                </label>
                <Input
                  value={editModal.title}
                  onChange={(e) => setEditModal(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={editModal.type === 'product' ? 'Enter product name' : editModal.type === 'persona' ? 'Enter persona name' : 'Enter title'}
                  data-testid="edit-modal-title-input"
                />
              </div>
              {/* Description field - only for Goal, Initiative, Epic (not Product or Persona) */}
              {editModal.type !== 'product' && editModal.type !== 'persona' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <Textarea
                    value={editModal.description}
                    onChange={(e) => setEditModal(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter description"
                    className="min-h-[80px]"
                    data-testid="edit-modal-description-input"
                  />
                </div>
              )}
              
              {/* Product-specific fields: Vision */}
              {editModal.type === 'product' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      High-Level Vision
                    </label>
                    <Textarea
                      value={editModal.high_level_vision}
                      onChange={(e) => setEditModal(prev => ({ ...prev, high_level_vision: e.target.value }))}
                      placeholder="What is the vision for this product?"
                      className="min-h-[80px]"
                      data-testid="edit-modal-vision-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Target Audience
                    </label>
                    <Textarea
                      value={editModal.target_audience}
                      onChange={(e) => setEditModal(prev => ({ ...prev, target_audience: e.target.value }))}
                      placeholder="Who is this product for?"
                      className="min-h-[60px]"
                      data-testid="edit-modal-audience-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Value Proposition
                    </label>
                    <Textarea
                      value={editModal.value_proposition}
                      onChange={(e) => setEditModal(prev => ({ ...prev, value_proposition: e.target.value }))}
                      placeholder="The unique value and key benefits users get from this product"
                      className="min-h-[60px]"
                      data-testid="edit-modal-value-prop-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Key Differentiators
                    </label>
                    <Textarea
                      value={editModal.key_differentiators}
                      onChange={(e) => setEditModal(prev => ({ ...prev, key_differentiators: e.target.value }))}
                      placeholder="What makes this product unique from competitors?"
                      className="min-h-[60px]"
                      data-testid="edit-modal-differentiators-input"
                    />
                  </div>
                </>
              )}
              
              {/* Initiative-specific fields */}
              {editModal.type === 'initiative' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Linked Goals
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {canvas.goals?.map(goal => {
                      const isSelected = editModal.goal_ids?.includes(goal.id);
                      return (
                        <Badge
                          key={goal.id}
                          variant={isSelected ? "default" : "outline"}
                          className={cn(
                            "cursor-pointer transition-colors",
                            isSelected 
                              ? "bg-green-600 hover:bg-green-700 text-white" 
                              : "hover:bg-green-50 dark:hover:bg-green-900/20"
                          )}
                          onClick={() => {
                            setEditModal(prev => ({
                              ...prev,
                              goal_ids: isSelected
                                ? prev.goal_ids.filter(id => id !== goal.id)
                                : [...(prev.goal_ids || []), goal.id]
                            }));
                          }}
                          data-testid={`initiative-goal-toggle-${goal.id}`}
                        >
                          {goal.title}
                        </Badge>
                      );
                    })}
                    {(!canvas.goals || canvas.goals.length === 0) && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No goals defined yet. Add goals first to link them.
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Persona-specific fields */}
              {editModal.type === 'persona' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Role
                    </label>
                    <Input
                      value={editModal.role}
                      onChange={(e) => setEditModal(prev => ({ ...prev, role: e.target.value }))}
                      placeholder="e.g., Product Manager, Developer, End User"
                      data-testid="edit-modal-role-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Goals
                    </label>
                    <Textarea
                      value={editModal.goals}
                      onChange={(e) => setEditModal(prev => ({ ...prev, goals: e.target.value }))}
                      placeholder="What are this persona's goals?"
                      className="min-h-[60px]"
                      data-testid="edit-modal-goals-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pain Points
                    </label>
                    <Textarea
                      value={editModal.pain_points}
                      onChange={(e) => setEditModal(prev => ({ ...prev, pain_points: e.target.value }))}
                      placeholder="What challenges does this persona face?"
                      className="min-h-[60px]"
                      data-testid="edit-modal-pain-points-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Context
                    </label>
                    <Textarea
                      value={editModal.context}
                      onChange={(e) => setEditModal(prev => ({ ...prev, context: e.target.value }))}
                      placeholder="Additional context about this persona"
                      className="min-h-[60px]"
                      data-testid="edit-modal-context-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEditModal}
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditModal}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-modal-save-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal (nested) */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete {editModal.type === 'persona' ? 'Persona' :
                      editModal.type === 'goal' ? 'Goal' :
                      editModal.type === 'initiative' ? 'Initiative' :
                      editModal.type === 'epic' ? 'Epic' : 'Item'}?
            </DialogTitle>
            <DialogDescription>
              {editModal.type === 'epic' 
                ? `This will permanently delete "${editModal.title || 'this epic'}" and all ${editModal.features?.length || 0} features within it. This action cannot be undone.`
                : editModal.type === 'initiative'
                ? `This will permanently delete "${editModal.title || 'this initiative'}". Epics linked to this initiative will become unlinked. This action cannot be undone.`
                : `This will permanently delete "${editModal.title || 'this item'}". This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFromModal}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewProductWizardPage;
